import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send } from "lucide-react";
import { RichEditor, htmlToText } from "@/components/RichEditor";
import { MailPreviewButton } from "@/components/MailPreview";
import { MailJobProgress, notifyMailJobs } from "@/components/MailJobProgress";
import { SchedulePicker, scheduleIso, emptySchedule } from "@/components/SchedulePicker";

export function PeopleMailDialog({ open, onOpenChange, recipients }) {
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("__free__");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [job, setJob] = useState(null);
  const [plan, setPlan] = useState(emptySchedule());
  const when = scheduleIso(plan);
  const planOk = !plan.on || (!!when && new Date(when) > new Date());

  useEffect(() => {
    if (open) { setJob(null); setPlan(emptySchedule()); api.get("/email-templates").then((r) => setTemplates(r.data)).catch(() => {}); }
  }, [open]);

  const chosen = templates.find((t) => t.id === tpl);
  const noMail = recipients.filter((r) => !r.email);

  const hasBody = !!body.replace(/<[^>]+>|&nbsp;|\s/g, "");
  const contentPayload = chosen ? { template_id: chosen.id } : { subject, body };

  const send = async () => {
    setSending(true);
    try {
      const payload = { person_ids: recipients.map((r) => r.id), ...contentPayload, scheduled_at: when };
      const { data } = await api.post("/people/email", payload);
      if (data.job_id) { setJob(data.job_id); notifyMailJobs(); setSubject(""); setBody(""); return; }
      if (data.sent.length) toast.success(`Gesendet an: ${data.sent.join(", ")}`);
      data.failed.forEach((f) => toast.error(`${f.name}: ${f.error}`));
      if (!data.failed.length) { onOpenChange(false); setSubject(""); setBody(""); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="people-mail-dialog">
        <DialogHeader>
          <DialogTitle>E-Mail an {recipients.length} {recipients.length === 1 ? "Person" : "Personen"}</DialogTitle>
          <DialogDescription>Versand über Microsoft 365. Jede Person erhält eine eigene Mail.</DialogDescription>
        </DialogHeader>
        {job ? <MailJobProgress jobId={job} onJob={setJob} /> : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5" data-testid="people-mail-recipients">
            {recipients.map((r) => (
              <span key={r.id} className={`text-xs px-2 py-1 rounded-md border ${r.email ? "bg-slate-50 border-slate-200 text-slate-700" : "bg-red-50 border-red-200 text-red-600"}`}>
                {r.name}{r.email ? "" : " (keine E-Mail)"}
              </span>
            ))}
          </div>
          <div>
            <Label>Vorlage</Label>
            <Select value={tpl} onValueChange={setTpl}>
              <SelectTrigger className="mt-1" data-testid="people-mail-template"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__free__">— Freier Text —</SelectItem>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {chosen ? (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
              <div className="font-medium text-slate-800">{chosen.subject}</div>
              <div className="mt-1 text-slate-600 whitespace-pre-line max-h-48 overflow-auto line-clamp-6">{htmlToText(chosen.body)}</div>
            </div>
          ) : (
            <>
              <div><Label>Betreff</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="people-mail-subject" /></div>
              <div><Label>Text</Label><RichEditor value={body} onChange={setBody} placeholders={["name", "vorname", "magazin", "absender"]} testid="people-mail-body" /></div>
            </>
          )}
          {noMail.length > 0 && <p className="text-xs text-red-600">{noMail.length} Person(en) ohne E-Mail werden übersprungen.</p>}
          <SchedulePicker value={plan} onChange={setPlan} />
        </div>
        )}
        <DialogFooter className="gap-2">
          {job ? <Button onClick={() => onOpenChange(false)} data-testid="people-mail-close">Schließen</Button> : <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <MailPreviewButton payload={{ ...contentPayload, person_id: recipients[0]?.id }} disabled={!chosen && (!subject.trim() || !hasBody)} testid="people-mail-preview" />
          <Button onClick={send} disabled={sending || !planOk || (!chosen && (!subject.trim() || !hasBody))} className="bg-blue-600 hover:bg-blue-700" data-testid="people-mail-send">
            <Send size={15} className="mr-1.5" /> {sending ? "Senden…" : when ? "Versand einplanen" : "Senden"}
          </Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
